import React, { FC } from 'react';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { fontSize } from 'styles/theme';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { CHECK_BLACK_ICON } from 'assets/icons/icons';
import { VideoPlayer } from 'components/video/VideoPlayer';
import { BLOG_LAPTOP } from 'assets/images/images';
import { SubSectionHeadline } from './parts/default.parts';

const SectionContainer = styled.section`
  background-color: ${colors.lightTeal};
  padding: 40px 0;

  &:before {
    background-color: ${colors.lightTeal};
    content: '';
    display: block;
    width: 100%;
  }
`;

const ContentContainerWrapper = styled.div<{ minHdDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ minHdDesktop }) => (minHdDesktop ? 'flex-start' : 'center')};
  align-self: center;
  margin: 0 auto;
`;

const ImageWrapper = styled.div<{ minHdDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  place-items: center;
  position: ${({ minHdDesktop }) => (minHdDesktop ? 'relative' : 'relative')};
  width: ${({ minHdDesktop }) => (minHdDesktop ? '' : '100%')};
`;

const LaptopContainer = styled.div<{ minHdDesktop: boolean }>`
  position: ${({ minHdDesktop }) => (minHdDesktop ? 'absolute' : '')};
  width: ${({ minHdDesktop }) => (minHdDesktop ? '1000px' : '100%')};
  right: ${({ minHdDesktop }) => (minHdDesktop ? '-200px' : '16px')};
  top: ${({ minHdDesktop }) => (minHdDesktop ? '40px' : '0')};
  z-index: 15;
`;

const StyledImage = styled.img<{ minHdDesktop: boolean }>`
  position: ${({ minHdDesktop }) => (minHdDesktop ? '' : '')};
  width: ${({ minHdDesktop }) => (minHdDesktop ? '1000px' : '100%')};
  right: ${({ minHdDesktop }) => (minHdDesktop ? '' : '16px')};
  top: ${({ minHdDesktop }) => (minHdDesktop ? '' : '0')};
  z-index: 15;
`;

const VideoContainer = styled.div<{ minHdDesktop: boolean }>`
  position: absolute;
  background: #000;
  width: ${({ minHdDesktop }) => (minHdDesktop ? '71%' : '71%')};
  height: ${({ minHdDesktop }) => (minHdDesktop ? '' : '75%')};
  top: ${({ minHdDesktop }) => (minHdDesktop ? '15%' : '12%')};
  right: 14.5%;
  z-index: 16;
`;

const HowOnlineTutoringWorksWrapper = styled.div<{ minHdDesktop: boolean }>`
  display: flex;
  justify-content: space-evenly;
  flex-direction: ${({ minHdDesktop }) => (minHdDesktop ? 'row' : 'column')};
  padding: ${({ minHdDesktop }) => (minHdDesktop ? 0 : '0 16px')};
  background: ${({ minHdDesktop }) => (minHdDesktop ? 'inherit' : '#f8f8f8')};
`;

const HowOnlineTutoringWorksBoxTopMain = styled.div<{ minHdDesktop: boolean; minTablet: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  height: ${({ minHdDesktop }) => (minHdDesktop ? '700px' : '')};
  background: #ffffff;
  border-radius: ${({ minHdDesktop }) => (minHdDesktop ? '6px' : '6px')};
  padding: ${({ minHdDesktop, minTablet }) =>
    minHdDesktop ? '16px 24px' : minTablet ? '80px 16px 40px 16px' : '40px 16px 40px 16px'};
  margin: ${({ minHdDesktop }) => (minHdDesktop ? '0 40px 0 0' : '')};
  flex-direction: ${({ minHdDesktop }) => (minHdDesktop ? 'row' : 'column')};
  justify-content: ${({ minHdDesktop }) => (minHdDesktop ? 'flex-start' : 'center')};
  align-items: ${({ minHdDesktop }) => (minHdDesktop ? 'center' : 'flex-start')};
  box-shadow: 0 0 5px 5px #00000022;
  z-index: 10;
`;

const BulletpointBoxTop = styled.div`
  margin: 16px 0;
  padding-left: 8px;
  font-size: 30px;
`;

const BulletpointItemTop = styled.span`
  font-size: ${fontSize.base};
  color: ${colors.blueGray};
  font-weight: 600;
  padding: 9px 0 6px 9px;
  display: block;
`;

export const VirtualTutoring: FC = () => {
  const { minTablet, minHdDesktop } = useBreakpoints();

  return (
    <SectionContainer id="virtuelles-klassenzimmer">
      <HowOnlineTutoringWorksWrapper minHdDesktop={minHdDesktop}>
        {minHdDesktop ? (
          <ImageWrapper minHdDesktop={minHdDesktop}>
            <LaptopContainer minHdDesktop={minHdDesktop}>
              <VideoContainer minHdDesktop={minHdDesktop}>
                <VideoPlayer id={'545088293'} ratio={0.56} />
              </VideoContainer>
              <StyledImage
                src={BLOG_LAPTOP}
                alt="laptop"
                loading="lazy"
                minHdDesktop={minHdDesktop}
              />
            </LaptopContainer>
          </ImageWrapper>
        ) : undefined}
        <HowOnlineTutoringWorksBoxTopMain minHdDesktop={minHdDesktop} minTablet={minTablet}>
          <ContentContainerWrapper minHdDesktop={minHdDesktop}>
            <SubSectionHeadline
              headline={translate(
                translationKeys.homepage.howElitetutoringWorks.bulletpointbox1.headline
              )}
              style={{ maxWidth: '650px', margin: 0 }}
              center={!minHdDesktop}
              noLineBreak={!minTablet}
              fullWidth
              noHeadlineColor
              level={2}
            />
            <BulletpointBoxTop>
              {Object.entries(
                translationKeys.homepage.howElitetutoringWorks.bulletpointbox1.content
              ).map(([translationKey, translationValue]) => (
                <BulletpointItemTop key={translationKey}>
                  <CustomIcon src={CHECK_BLACK_ICON} width={20} style={{ paddingRight: '4px' }} />{' '}
                  {translate(translationValue)}
                </BulletpointItemTop>
              ))}
            </BulletpointBoxTop>
          </ContentContainerWrapper>
          {minHdDesktop ? undefined : (
            <ImageWrapper minHdDesktop={minHdDesktop}>
              <VideoContainer minHdDesktop={minHdDesktop}>
                <VideoPlayer id={'545088293'} ratio={0.56} stretchWidth />
              </VideoContainer>
              <StyledImage
                src={BLOG_LAPTOP}
                alt="laptop"
                loading="lazy"
                minHdDesktop={minHdDesktop}
              />
            </ImageWrapper>
          )}
        </HowOnlineTutoringWorksBoxTopMain>
      </HowOnlineTutoringWorksWrapper>
    </SectionContainer>
  );
};
